import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { moveTypesApi } from '../services/api';
import { MoveType, CreateMoveTypeData } from '../types';

const MoveTypesManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingMoveType, setEditingMoveType] = useState<MoveType | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: moveTypesData, isLoading } = useQuery({
    queryKey: ['moveTypes', { page, search }],
    queryFn: () => moveTypesApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateMoveTypeData>();

  const createMutation = useMutation({
    mutationFn: moveTypesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moveTypes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateMoveTypeData }) =>
      moveTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moveTypes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setEditingMoveType(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: moveTypesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moveTypes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const onSubmit = (data: CreateMoveTypeData) => {
    if (editingMoveType) {
      updateMutation.mutate({ id: editingMoveType.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (moveType: MoveType) => {
    setEditingMoveType(moveType);
    reset({
      name: moveType.name,
      description: moveType.description || '',
    });
    setShowForm(true);
  };

  const handleDelete = (moveType: MoveType) => {
    if (window.confirm(`Are you sure you want to delete "${moveType.name}"?`)) {
      deleteMutation.mutate(moveType.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMoveType(null);
    reset();
  };

  return (
    <div className="move-types-management">
      <div className="page-header">
        <h1>Move Types Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add New Move Type
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingMoveType ? 'Edit Move Type' : 'Create New Move Type'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                id="name"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingMoveType ? 'Update' : 'Create'} Move Type
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search move types..."
          aria-label="Search move types"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading move types...</div>
      ) : moveTypesData && moveTypesData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Moves Count</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {moveTypesData.data.map((moveType) => (
                  <tr key={moveType.id}>
                    <td>
                      <strong>{moveType.name}</strong>
                    </td>
                    <td>{moveType.description || '-'}</td>
                    <td>{moveType.movesCount || 0}</td>
                    <td>{new Date(moveType.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(moveType)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(moveType)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {moveTypesData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: moveTypesData.pagination.pages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn btn-small ${
                      pageNum === page ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No move types found. Create your first move type!</p>
        </div>
      )}
    </div>
  );
};

export default MoveTypesManagement;